import { SupabaseDB } from '@/utils/supabase';
import type {
  DeviceOverview,
  UserStatistics,
  MonthlyRevenueItem,
  EnergyConsumption,
  UserRegistrationChartData,
  UserRegistrationData,
  EnergyConsumptionCategory,
  RevenueBarChartData,
  RevenueBarData,
  StationRevenueRanking,
  DashboardSummary,
  DashboardFilter,
} from '@/types/dashboard';

// Normalize a DB timestamp to milliseconds: payments use ms (13 digits),
// users mix seconds (10 digits) and ms (13 digits)
const toMs = (v: any): number => {
  const n = parseInt(v);
  if (!n) return 0;
  return n < 1e12 ? n * 1000 : n;
};

// Helper function to get date range
const getDateRange = (period: 'today' | 'week' | 'month' | 'year') => {
  const now = new Date();
  const start = new Date();
  const end = new Date();

  switch (period) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'week':
      start.setDate(now.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      break;
    case 'month':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'year':
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      break;
  }

  return {
    start: start.getTime(),
    end: end.getTime(),
  };
};

// Cache for payments data to avoid duplicate queries
let paymentsCache: any[] | null = null;
let paymentsCacheTime: number = 0;
const CACHE_DURATION = 60000; // 1 minute

let bossUserIdsCache: Set<string> | null = null;
let bossUserIdsCacheTime: number = 0;

const getBossUserIds = async (): Promise<Set<string>> => {
  const now = Date.now();
  if (bossUserIdsCache && (now - bossUserIdsCacheTime) < CACHE_DURATION) {
    return bossUserIdsCache;
  }
  let ids = new Set<string>();
  try {
    const { data: roles } = await SupabaseDB.customQuery('role')
      .select('role_id')
      .eq('role_name', 'Boss TPK EV');
    const bossRoleId = (roles || [])[0]?.role_id;
    if (bossRoleId) {
      const { data: mappings } = await SupabaseDB.customQuery('user_roles')
        .select('user_id')
        .eq('role_id', bossRoleId);
      ids = new Set((mappings || []).map((m: any) => m.user_id));
    }
  } catch (error) {
    console.error('Error fetching boss user ids:', error);
  }
  bossUserIdsCache = ids;
  bossUserIdsCacheTime = now;
  return ids;
};

// Helper to get all completed payments with cache
const getCachedPayments = async (): Promise<any[]> => {
  const now = Date.now();
  
  // Return cached data if still valid
  if (paymentsCache && (now - paymentsCacheTime) < CACHE_DURATION) {
    console.log('📦 Using cached payments data');
    return paymentsCache;
  }
  
  console.log('🔄 Fetching fresh payments data');
  
  // Get payments from last 13 months for all calculations
  const thirteenMonthsAgo = new Date();
  thirteenMonthsAgo.setMonth(thirteenMonthsAgo.getMonth() - 13);
  
  const { data: payments, error } = await SupabaseDB.customQuery('payments')
    .select('money, watt_spent, station_id, created_at, buyer_id')
    .eq('status_payment', true)
    .gte('created_at', thirteenMonthsAgo.getTime());
  
  if (error) {
    console.error('Error fetching payments:', error);
    return [];
  }
  
  const bossIds = await getBossUserIds();
  paymentsCache = (payments || []).filter((p: any) => !bossIds.has(p.buyer_id));
  paymentsCacheTime = now;
  
  return paymentsCache;
};

// Helper to get month label
const getMonthLabel = (date: Date): string => {
  const month = date.getMonth() + 1;
  return `Th${month}`;
};

/**
 * 1. Get Device Overview
 * Tổng quan thiết bị: total, active, offline, charging
 */
export const getDeviceOverview = async (): Promise<DeviceOverview> => {
  try {
    // Get all devices (no status column in devices table)
    const { data: devices, error: devicesError } = await SupabaseDB.select(
      'devices',
      'device_id'
    );

    if (devicesError) throw devicesError;

    // Get device statuses from last_status_device
    const { data: statuses, error: statusError } = await SupabaseDB.select(
      'last_status_device',
      'device_id, last_message'
    );

    if (statusError) throw statusError;

    const total = devices?.length || 0;
    let active = 0;
    let offline = 0;
    let charging = 0;

    // Map device status from last_message
    const statusMap = new Map(statuses?.map((s: any) => [s.device_id, s.last_message]) || []);

    devices?.forEach((device: any) => {
      const lastMessage = statusMap.get(device.device_id);
      
      if (!lastMessage) {
        // No status data = offline
        offline++;
        return;
      }

      // Check if device is charging
      if (lastMessage.status === 'charging' || lastMessage.is_charging || lastMessage.charging) {
        charging++;
      } 
      // Check if device is online/active
      else if (lastMessage.status === 'online' || lastMessage.status === 'active' || lastMessage.online) {
        active++;
      } 
      // Otherwise offline
      else {
        offline++;
      }
    });

    return { total, active, offline, charging };
  } catch (error) {
    console.error('Error fetching device overview:', error);
    return { total: 0, active: 0, offline: 0, charging: 0 };
  }
};

/**
 * 2. Get User Statistics
 * Thống kê người dùng: total, new today, new this week, new this month
 */
export const getUserStatistics = async (): Promise<UserStatistics> => {
  try {
    // Get all users
    const { data: allUsers, error: allError } = await SupabaseDB.select('users', 'user_id, created_at');

    if (allError) throw allError;

    const bossIds = await getBossUserIds();
    const users = (allUsers || []).filter((u: any) => !bossIds.has(u.user_id));
    const total = users.length;

    const today = getDateRange('today');
    const week = getDateRange('week');
    const month = getDateRange('month');

    let newToday = 0;
    let newThisWeek = 0;
    let newThisMonth = 0;

    users.forEach((user: any) => {
      const createdAt = toMs(user.created_at);
      if (createdAt >= today.start) newToday++;
      if (createdAt >= week.start) newThisWeek++;
      if (createdAt >= month.start) newThisMonth++;
    });

    return { total, newToday, newThisWeek, newThisMonth };
  } catch (error) {
    console.error('Error fetching user statistics:', error);
    return { total: 0, newToday: 0, newThisWeek: 0, newThisMonth: 0 };
  }
};

/**
 * 3. Get Monthly Revenue
 * Doanh thu và điện tiêu thụ 12 tháng gần nhất
 */
export const getMonthlyRevenue = async (): Promise<MonthlyRevenueItem[]> => {
  try {
    const result: MonthlyRevenueItem[] = [];
    const now = new Date();
    
    // Get cached payments data
    const allPayments = await getCachedPayments();

    // Get last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
      
      const startTimestamp = date.getTime();
      const endTimestamp = nextMonth.getTime();

      // Filter payments for this month from cached data
      const payments = allPayments.filter((p: any) => {
        const createdAt = toMs(p.created_at);
        return createdAt >= startTimestamp && createdAt < endTimestamp;
      });

      const revenue = payments.reduce((sum: number, p: any) => sum + (parseFloat(p.money) || 0), 0);
      const energy = payments.reduce((sum: number, p: any) => sum + (parseFloat(p.watt_spent) || 0), 0) / 1000; // Convert to kWh

      result.push({
        month: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        monthLabel: getMonthLabel(date),
        revenue: Math.round(revenue),
        energy: Math.round(energy * 10) / 10, // Round to 1 decimal
      });
    }

    return result;
  } catch (error) {
    console.error('Error fetching monthly revenue:', error);
    return [];
  }
};

/**
 * 4. Get Energy Consumption
 * Điện tiêu thụ theo ngày/tuần/tháng
 */
export const getEnergyConsumption = async (): Promise<EnergyConsumption> => {
  try {
    const now = new Date();
    const today = getDateRange('today');
    const week = getDateRange('week');
    const month = getDateRange('month');
    
    // Get previous month for comparison
    const prevMonth = new Date();
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const prevMonthRange = {
      start: new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1).getTime(),
      end: new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0).getTime(),
    };
    
    // Get cached payments data
    const allPayments = await getCachedPayments();

    const calculateEnergy = (startTimestamp: number, endTimestamp?: number) => {
      const filtered = allPayments.filter((p: any) => {
        const createdAt = toMs(p.created_at);
        if (endTimestamp) {
          return createdAt >= startTimestamp && createdAt <= endTimestamp;
        }
        return createdAt >= startTimestamp;
      });
      const totalWatt = filtered.reduce((sum: number, p: any) => sum + (parseFloat(p.watt_spent) || 0), 0);
      return totalWatt / 1000; // Convert to kWh
    };

    const todayEnergy = calculateEnergy(today.start, today.end);
    const weekEnergy = calculateEnergy(week.start);
    const monthEnergy = calculateEnergy(month.start);
    const prevMonthEnergy = calculateEnergy(prevMonthRange.start, prevMonthRange.end);

    const change = prevMonthEnergy > 0 ? ((monthEnergy - prevMonthEnergy) / prevMonthEnergy) * 100 : 0;

    return {
      today: Math.round(todayEnergy * 10) / 10,
      thisWeek: Math.round(weekEnergy * 10) / 10,
      thisMonth: Math.round(monthEnergy * 10) / 10,
      change: Math.round(change * 10) / 10,
    };
  } catch (error) {
    console.error('Error fetching energy consumption:', error);
    return { today: 0, thisWeek: 0, thisMonth: 0, change: 0 };
  }
};

/**
 * 5. Get User Registration Chart Data
 * Biểu đồ đăng ký khách hàng - so sánh tháng hiện tại vs tháng trước
 */
export const getUserRegistrationChart = async (month?: string): Promise<UserRegistrationChartData> => {
  try {
    const now = new Date();
    const currentMonth = month
      ? new Date(parseInt(month.split('-')[0]), parseInt(month.split('-')[1]) - 1, 1)
      : new Date(now.getFullYear(), now.getMonth(), 1);
    const previousMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);

    // Users mix seconds and ms timestamps, so a single DB range cannot work:
    // fetch all users (small table) and filter in JS with toMs
    const { data: allRegUsers } = await SupabaseDB.select('users', 'user_id, created_at');
    const bossIds = await getBossUserIds();
    const regUsers = (allRegUsers || []).filter((u: any) => !bossIds.has(u.user_id));
    const currentUsers = regUsers.filter((u: any) => {
      const t = toMs(u.created_at);
      return t >= currentMonth.getTime() && t < nextMonth.getTime();
    });
    const previousUsers = regUsers.filter((u: any) => {
      const t = toMs(u.created_at);
      return t >= previousMonth.getTime() && t < currentMonth.getTime();
    });

    // Count by day
    const currentDays = new Map<number, number>();
    const previousDays = new Map<number, number>();

    currentUsers?.forEach((user: any) => {
      const date = new Date(toMs(user.created_at));
      const day = date.getDate();
      currentDays.set(day, (currentDays.get(day) || 0) + 1);
    });

    previousUsers?.forEach((user: any) => {
      const date = new Date(toMs(user.created_at));
      const day = date.getDate();
      previousDays.set(day, (previousDays.get(day) || 0) + 1);
    });

    // Get max days in both months
    const daysInCurrentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const daysInPreviousMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0).getDate();
    const maxDays = Math.max(daysInCurrentMonth, daysInPreviousMonth);

    // Build chart data
    const data: UserRegistrationData[] = [];
    for (let day = 1; day <= maxDays; day++) {
      data.push({
        date: String(day),
        currentMonth: currentDays.get(day) || 0,
        previousMonth: previousDays.get(day) || 0,
      });
    }

    const totalCurrent = currentUsers?.length || 0;
    const totalPrevious = previousUsers?.length || 0;
    const growth = totalPrevious > 0 ? ((totalCurrent - totalPrevious) / totalPrevious) * 100 : 0;

    return {
      currentMonthLabel: `Tháng ${currentMonth.getMonth() + 1}/${currentMonth.getFullYear()}`,
      previousMonthLabel: `Tháng ${previousMonth.getMonth() + 1}/${previousMonth.getFullYear()}`,
      data,
      totalCurrent,
      totalPrevious,
      growth: Math.round(growth * 10) / 10,
    };
  } catch (error) {
    console.error('Error fetching user registration chart:', error);
    return {
      currentMonthLabel: '',
      previousMonthLabel: '',
      data: [],
      totalCurrent: 0,
      totalPrevious: 0,
      growth: 0,
    };
  }
};

/**
 * 6. Get Energy Consumption Pie Chart
 * Biểu đồ tròn phân loại mức sạc (nhanh, thường, chậm)
 */
export const getEnergyConsumptionPieChart = async (): Promise<EnergyConsumptionCategory[]> => {
  try {
    const month = getDateRange('month');
    
    // Get cached payments data
    const allPayments = await getCachedPayments();
    
    // Filter payments for this month
    const payments = allPayments.filter((p: any) => toMs(p.created_at) >= month.start);

    if (payments.length === 0) {
      return [];
    }

    // Categorize by watt_spent
    // Sạc nhanh: > 3000W, Sạc thường: 1500-3000W, Sạc chậm: < 1500W
    let fast = 0;
    let normal = 0;
    let slow = 0;

    payments.forEach((p: any) => {
      const watts = parseFloat(p.watt_spent) || 0;
      if (watts > 3000) {
        fast += watts;
      } else if (watts >= 1500) {
        normal += watts;
      } else {
        slow += watts;
      }
    });

    const total = fast + normal + slow;
    if (total === 0) return [];

    // Convert to kWh
    fast = fast / 1000;
    normal = normal / 1000;
    slow = slow / 1000;
    const totalKWh = fast + normal + slow;

    const categories: EnergyConsumptionCategory[] = [
      {
        category: 'Sạc nhanh (>3kW)',
        value: Math.round(fast * 10) / 10,
        percentage: Math.round((fast / totalKWh) * 100 * 10) / 10,
        color: '#ff4d4f',
      },
      {
        category: 'Sạc thường (1.5-3kW)',
        value: Math.round(normal * 10) / 10,
        percentage: Math.round((normal / totalKWh) * 100 * 10) / 10,
        color: '#faad14',
      },
      {
        category: 'Sạc chậm (<1.5kW)',
        value: Math.round(slow * 10) / 10,
        percentage: Math.round((slow / totalKWh) * 100 * 10) / 10,
        color: '#52c41a',
      },
    ];

    return categories.filter(c => c.value > 0);
  } catch (error) {
    console.error('Error fetching energy consumption pie chart:', error);
    return [];
  }
};

/**
 * 6b. Get Station Revenue Pie Chart
 * Bieu do tron doanh thu theo tram (thang hien tai). Dung thay cho pie dien
 * tieu thu theo loai sac vi watt_spent gan nhu trong (chi 1 giao dich co gia
 * tri), trong khi doanh thu theo tram co du lieu that. Nap tien (wallet) bi
 * loai vi khong thuoc tram nao.
 */
export const getStationRevenuePieChart = async (): Promise<EnergyConsumptionCategory[]> => {
  try {
    const ranking = await getStationRevenueRanking(10);
    const active = ranking.filter((r: any) => r.revenue > 0);
    const total = active.reduce((sum: number, r: any) => sum + r.revenue, 0);
    if (total === 0) return [];
    const palette = ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2', '#eb2f96', '#f5222d', '#a0d911', '#2f54eb'];
    return active.map((r: any, index: number) => ({
      category: r.stationName,
      value: r.revenue,
      percentage: Math.round((r.revenue / total) * 100 * 10) / 10,
      color: palette[index % palette.length],
    }));
  } catch (error) {
    console.error('Error fetching station revenue pie chart:', error);
    return [];
  }
};

/**
 * 7. Get Revenue Bar Chart
 * Biểu đồ cột doanh thu tuần/tháng
 */
export const getRevenueBarChart = async (type: 'week' | 'month' = 'month'): Promise<RevenueBarChartData> => {
  try {
    const data: RevenueBarData[] = [];
    const now = new Date();
    
    // Get cached payments data
    const allPayments = await getCachedPayments();

    if (type === 'week') {
      // Last 8 weeks
      for (let i = 7; i >= 0; i--) {
        const weekEnd = new Date(now);
        weekEnd.setDate(now.getDate() - (i * 7));
        const weekStart = new Date(weekEnd);
        weekStart.setDate(weekEnd.getDate() - 6);

        const startTimestamp = weekStart.getTime();
        const endTimestamp = weekEnd.getTime();

        const payments = allPayments.filter((p: any) => {
          const createdAt = toMs(p.created_at);
          return createdAt >= startTimestamp && createdAt <= endTimestamp;
        });

        const revenue = payments.reduce((sum: number, p: any) => sum + (parseFloat(p.money) || 0), 0);

        data.push({
          period: `Tuần ${8 - i}`,
          revenue: Math.round(revenue),
          label: `${weekStart.getDate()}/${weekStart.getMonth() + 1} - ${weekEnd.getDate()}/${weekEnd.getMonth() + 1}`,
        });
      }
    } else {
      // Last 12 months
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);

        const startTimestamp = date.getTime();
        const endTimestamp = nextMonth.getTime();

        const payments = allPayments.filter((p: any) => {
          const createdAt = toMs(p.created_at);
          return createdAt >= startTimestamp && createdAt < endTimestamp;
        });

        const revenue = payments.reduce((sum: number, p: any) => sum + (parseFloat(p.money) || 0), 0);

        data.push({
          period: getMonthLabel(date),
          revenue: Math.round(revenue),
          label: `Tháng ${date.getMonth() + 1}/${date.getFullYear()}`,
        });
      }
    }

    const total = data.reduce((sum, item) => sum + item.revenue, 0);
    const average = data.length > 0 ? total / data.length : 0;

    return {
      type,
      data,
      total: Math.round(total),
      average: Math.round(average),
    };
  } catch (error) {
    console.error('Error fetching revenue bar chart:', error);
    return { type, data: [], total: 0, average: 0 };
  }
};

const getAllPaidPayments = async (): Promise<any[]> => {
  const { data, error } = await SupabaseDB.customQuery('payments')
    .select('money, created_at, buyer_id, station_id')
    .eq('status_payment', true);
  if (error) {
    console.error('Error fetching all payments:', error);
    return [];
  }
  const bossIds = await getBossUserIds();
  return (data || []).filter((p: any) => !bossIds.has(p.buyer_id));
};

export const getAvailableRevenueMonths = async (): Promise<{ month: string; label: string; revenue: number }[]> => {
  try {
    const payments = await getAllPaidPayments();
    const byMonth = new Map<string, number>();
    payments.forEach((p: any) => {
      const d = new Date(toMs(p.created_at));
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      byMonth.set(key, (byMonth.get(key) || 0) + (parseFloat(p.money) || 0));
    });
    return Array.from(byMonth.entries())
      .map(([month, revenue]) => {
        const [y, m] = month.split('-');
        return { month, label: `Tháng ${parseInt(m)}/${y}`, revenue: Math.round(revenue) };
      })
      .sort((a, b) => (a.month < b.month ? 1 : -1));
  } catch (error) {
    console.error('Error fetching available revenue months:', error);
    return [];
  }
};

export const getRevenueWeeksOfMonth = async (month: string): Promise<RevenueBarChartData> => {
  try {
    const [y, m] = month.split('-').map((v) => parseInt(v));
    const monthIdx = m - 1;
    const daysInMonth = new Date(y, monthIdx + 1, 0).getDate();
    const payments = await getAllPaidPayments();
    const data: RevenueBarData[] = [];
    for (let startDay = 1, weekNo = 1; startDay <= daysInMonth; startDay += 7, weekNo++) {
      const endDay = Math.min(startDay + 6, daysInMonth);
      const weekStart = new Date(y, monthIdx, startDay).getTime();
      const weekEnd = new Date(y, monthIdx, endDay + 1).getTime();
      const revenue = payments
        .filter((p: any) => {
          const t = toMs(p.created_at);
          return t >= weekStart && t < weekEnd;
        })
        .reduce((sum: number, p: any) => sum + (parseFloat(p.money) || 0), 0);
      data.push({
        period: `Tuần ${weekNo}`,
        revenue: Math.round(revenue),
        label: `${startDay}/${m} - ${endDay}/${m}`,
      });
    }
    const total = data.reduce((sum, item) => sum + item.revenue, 0);
    const average = data.length > 0 ? total / data.length : 0;
    return { type: 'month', data, total: Math.round(total), average: Math.round(average) };
  } catch (error) {
    console.error('Error fetching revenue weeks of month:', error);
    return { type: 'month', data: [], total: 0, average: 0 };
  }
};

export const getAvailableRegistrationMonths = async (): Promise<{ month: string; label: string; count: number }[]> => {
  try {
    const { data: allUsers } = await SupabaseDB.select('users', 'user_id, created_at');
    const bossIds = await getBossUserIds();
    const byMonth = new Map<string, number>();
    (allUsers || [])
      .filter((u: any) => !bossIds.has(u.user_id))
      .forEach((u: any) => {
        const d = new Date(toMs(u.created_at));
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        byMonth.set(key, (byMonth.get(key) || 0) + 1);
      });
    return Array.from(byMonth.entries())
      .map(([month, count]) => {
        const [y, m] = month.split('-');
        return { month, label: `Tháng ${parseInt(m)}/${y}`, count };
      })
      .sort((a, b) => (a.month < b.month ? 1 : -1));
  } catch (error) {
    console.error('Error fetching available registration months:', error);
    return [];
  }
};

export const getStationRevenuePieChartForMonth = async (month: string): Promise<EnergyConsumptionCategory[]> => {
  try {
    const [y, m] = month.split('-').map((v) => parseInt(v));
    const monthStart = new Date(y, m - 1, 1).getTime();
    const monthEnd = new Date(y, m, 1).getTime();
    const { data: stations } = await SupabaseDB.select('stations', 'station_id, station_name');
    const payments = await getAllPaidPayments();
    const byStation = new Map<string, number>();
    payments
      .filter((p: any) => {
        const t = toMs(p.created_at);
        return p.station_id && p.station_id !== 'wallet' && t >= monthStart && t < monthEnd;
      })
      .forEach((p: any) => {
        byStation.set(p.station_id, (byStation.get(p.station_id) || 0) + (parseFloat(p.money) || 0));
      });
    const stationName = new Map((stations || []).map((s: any) => [s.station_id, s.station_name]));
    const total = Array.from(byStation.values()).reduce((sum, v) => sum + v, 0);
    if (total === 0) return [];
    const palette = ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1', '#13c2c2', '#eb2f96', '#f5222d', '#a0d911', '#2f54eb'];
    return Array.from(byStation.entries())
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([stationId, revenue], index) => ({
        category: stationName.get(stationId) || stationId,
        value: Math.round(revenue),
        percentage: Math.round((revenue / total) * 100 * 10) / 10,
        color: palette[index % palette.length],
      }));
  } catch (error) {
    console.error('Error fetching station revenue pie chart for month:', error);
    return [];
  }
};

/**
 * 8. Get Station Revenue Ranking
 * Bảng xếp hạng doanh thu trạm sạc
 */
export const getStationRevenueRanking = async (limit: number = 10): Promise<StationRevenueRanking[]> => {
  try {
    const month = getDateRange('month');
    const prevMonth = new Date();
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const prevMonthRange = {
      start: new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1).getTime(),
      end: new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0).getTime(),
    };

    // Get all stations
    const { data: stations } = await SupabaseDB.select('stations', 'station_id, station_name');

    if (!stations || stations.length === 0) return [];
    
    // Get cached payments data
    const allPayments = await getCachedPayments();

    // Get payments for each station (current month)
    const stationRevenues: any[] = [];

    for (const station of stations) {
      // Filter current month payments for this station
      const currentPayments = allPayments.filter((p: any) => {
        return p.station_id === station.station_id && toMs(p.created_at) >= month.start;
      });

      // Filter previous month payments for this station
      const prevPayments = allPayments.filter((p: any) => {
        const createdAt = toMs(p.created_at);
        return p.station_id === station.station_id && 
               createdAt >= prevMonthRange.start && 
               createdAt <= prevMonthRange.end;
      });

      const revenue = currentPayments.reduce((sum: number, p: any) => sum + (parseFloat(p.money) || 0), 0);
      const energy = currentPayments.reduce((sum: number, p: any) => sum + (parseFloat(p.watt_spent) || 0), 0) / 1000;
      const transactions = currentPayments.length;

      const prevRevenue = prevPayments.reduce((sum: number, p: any) => sum + (parseFloat(p.money) || 0), 0);
      const growth = prevRevenue > 0 ? ((revenue - prevRevenue) / prevRevenue) * 100 : 0;

      stationRevenues.push({
        stationId: station.station_id,
        stationName: station.station_name,
        revenue: Math.round(revenue),
        energy: Math.round(energy * 10) / 10,
        transactions,
        growth: Math.round(growth * 10) / 10,
        trend: growth > 5 ? 'up' : growth < -5 ? 'down' : 'stable',
      });
    }

    // Sort by revenue descending
    stationRevenues.sort((a, b) => b.revenue - a.revenue);

    // Add rank
    const ranking = stationRevenues.slice(0, limit).map((station, index) => ({
      rank: index + 1,
      ...station,
    }));

    return ranking;
  } catch (error) {
    console.error('Error fetching station revenue ranking:', error);
    return [];
  }
};

/**
 * Get Dashboard Summary
 * Tổng hợp tất cả dữ liệu cho dashboard
 */
export const getDashboardSummary = async (filter?: DashboardFilter): Promise<DashboardSummary> => {
  try {
    const [
      deviceOverview,
      userStatistics,
      monthlyRevenue,
      energyConsumption,
      userRegistrationChart,
      energyConsumptionPie,
      revenueBarChart,
      stationRanking,
    ] = await Promise.all([
      getDeviceOverview(),
      getUserStatistics(),
      getMonthlyRevenue(),
      getEnergyConsumption(),
      getUserRegistrationChart(),
      getEnergyConsumptionPieChart(),
      getRevenueBarChart('month'),
      getStationRevenueRanking(10),
    ]);

    return {
      deviceOverview,
      userStatistics,
      monthlyRevenue,
      energyConsumption,
      userRegistrationChart,
      energyConsumptionPie,
      revenueBarChart,
      stationRanking,
      lastUpdated: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error fetching dashboard summary:', error);
    throw error;
  }
};

export const dashboardService = {
  getDeviceOverview,
  getUserStatistics,
  getMonthlyRevenue,
  getEnergyConsumption,
  getUserRegistrationChart,
  getEnergyConsumptionPieChart,
  getStationRevenuePieChart,
  getRevenueBarChart,
  getAvailableRevenueMonths,
  getRevenueWeeksOfMonth,
  getAvailableRegistrationMonths,
  getStationRevenuePieChartForMonth,
  getStationRevenueRanking,
  getDashboardSummary,
};
